import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";

const TAB_GAP_PX = 4;

interface VisibleTabCount {
  rowRef: RefObject<HTMLDivElement | null>;
  measureRef: RefObject<HTMLDivElement | null>;
  visibleCount: number;
}

export function useVisibleTabCount(tabLabels: string[]): VisibleTabCount {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabLabels.length);
  const measureKey = tabLabels.join("\u0000");

  const updateVisibleCount = useCallback(() => {
    const tabsList = rowRef.current?.querySelector<HTMLElement>('[data-slot="tabs-list"]');
    const measure = measureRef.current;

    if (!tabsList || !measure) return;

    const measuredItems = Array.from(measure.children) as HTMLElement[];

    if (measuredItems.length < 2) return;

    const moreTriggerWidth = measuredItems[measuredItems.length - 1].offsetWidth;
    const tabWidths = measuredItems.slice(0, -1).map((item) => item.offsetWidth);

    setVisibleCount(computeVisibleCount(tabWidths, moreTriggerWidth, tabsList.clientWidth));
  }, []);

  useLayoutEffect(() => {
    const animationFrame = requestAnimationFrame(updateVisibleCount);
    const tabsList = rowRef.current?.querySelector<HTMLElement>('[data-slot="tabs-list"]');
    const resizeObserver = new ResizeObserver(updateVisibleCount);

    if (tabsList) resizeObserver.observe(tabsList);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [updateVisibleCount, measureKey]);

  return { rowRef, measureRef, visibleCount };
}

function computeVisibleCount(tabWidths: number[], moreTriggerWidth: number, availableWidth: number): number {
  if (availableWidth <= 0) {
    return tabWidths.length;
  }

  const totalWidth = tabWidths.reduce((sum, width) => sum + width, 0) + TAB_GAP_PX * (tabWidths.length - 1);
  if (totalWidth <= availableWidth) {
    return tabWidths.length;
  }

  let usedWidth = moreTriggerWidth;
  let count = 0;

  for (const width of tabWidths) {
    const nextWidth = usedWidth + TAB_GAP_PX + width;

    if (nextWidth > availableWidth) break;

    usedWidth = nextWidth;
    count += 1;
  }

  return Math.max(count, 1);
}
